import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Hex color string. Accepts `#RGB`, `#RRGGBB`, and `#RRGGBBAA`. Validation is
 * loose on purpose - the source of truth is the UI color picker, the field
 * is operator-editable, and a malformed value should fall back to the
 * inference helper rather than reject the whole status edit.
 */
export const HexColorSchema = z
  .string()
  .trim()
  .regex(
    /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/,
    'Must be a hex color like #3b82f6',
  );

/**
 * Query Status master (Masters → Query Statuses). The lead-pipeline stage
 * (e.g. "new", "quoted", "won", "lost"). **Self-referential**: a status may
 * have a `parentId` pointing at another status, which is how sub-statuses are
 * modelled (e.g. parent "Quoted" → child "Quoted - negotiation") - there is no
 * separate sub-status table. `bucket` groups statuses for funnel rollups; the
 * `isWon` / `isLost` flags mark terminal outcomes. See ADR-0016 / ADR-0025.
 *
 * `name` is NOT unique (a child can share a display name across parents);
 * identity is the unique `code`.
 */

/** Slim parent (self) reference embedded in list/detail rows. */
const QueryStatusRefSchema = z.object({ id: UuidSchema, code: z.string(), name: z.string() });

export const QueryStatusListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  code: z.string(),
  name: z.string(),
  /** Non-null when this status is a sub-status of another. */
  parent: QueryStatusRefSchema.nullable(),
  bucket: z.string().nullable(),
  /** Editable hex color (e.g. "#3b82f6"). NULL → UI infers from bucket/name. */
  color: z.string().nullable(),
  isWon: z.boolean(),
  isLost: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type QueryStatusListItem = z.infer<typeof QueryStatusListItemSchema>;

export const QueryStatusListQuerySchema = PaginationQuerySchema.extend({
  parentId: UuidSchema.optional(),
  bucket: z.string().trim().min(1).max(60).optional(),
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type QueryStatusListQuery = z.infer<typeof QueryStatusListQuerySchema>;

export const QueryStatusListResponseSchema = paginatedResponseSchema(QueryStatusListItemSchema);
export type QueryStatusListResponse = z.infer<typeof QueryStatusListResponseSchema>;

export const QueryStatusDetailSchema = QueryStatusListItemSchema.extend({
  description: z.string().nullable(),
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type QueryStatusDetail = z.infer<typeof QueryStatusDetailSchema>;

const QueryStatusCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  /** Optional parent status (makes this a sub-status). */
  parentId: UuidSchema.nullable().optional(),
  description: z.string().trim().max(500).nullable().optional(),
  /** Funnel bucket, e.g. "OPEN" / "WON" / "LOST". */
  bucket: z.string().trim().max(60).nullable().optional(),
  /** Stage indicator hex color. NULL clears it (UI falls back to inference). */
  color: HexColorSchema.nullable().optional(),
  isWon: z.boolean().optional(),
  isLost: z.boolean().optional(),
  legacyId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateQueryStatusRequestSchema = QueryStatusCoreFieldsSchema;
export type CreateQueryStatusRequest = z.infer<typeof CreateQueryStatusRequestSchema>;

export const UpdateQueryStatusRequestSchema = QueryStatusCoreFieldsSchema.partial();
export type UpdateQueryStatusRequest = z.infer<typeof UpdateQueryStatusRequestSchema>;

// ---------------------------------------------------------------------------
// Options (slim dropdown shape for the self-referential parent picker)
// ---------------------------------------------------------------------------

export const QueryStatusOptionSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  /** Surfaced in /options so dropdown chips show the same color as list rows. */
  color: z.string().nullable(),
});
export type QueryStatusOption = z.infer<typeof QueryStatusOptionSchema>;

export const QueryStatusOptionListSchema = z.array(QueryStatusOptionSchema);
export type QueryStatusOptionList = z.infer<typeof QueryStatusOptionListSchema>;

export const QueryStatusOptionsQuerySchema = z.object({
  q: z.string().trim().min(1).max(120).optional(),
  activeOnly: BooleanQueryParamSchema.optional(),
});
export type QueryStatusOptionsQuery = z.infer<typeof QueryStatusOptionsQuerySchema>;
